// Generated by GitHub Copilot
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Back-compat resolver for legacy `web+ynteq://` deep links printed onto v1 QR
 * stickers. v1 QR codes encode `<origin>/handle?url=<encoded web+ynteq:// link>`;
 * this public route decodes that link and forwards it to the matching v2 route so
 * already-printed codes keep working. It must NOT assume an authenticated user —
 * the public work-request form mints its own guest token.
 *
 * Only the work-request deep link carries a company id and can be mapped
 * deterministically; any other legacy pattern falls back to the app root.
 */
export const Route = createFileRoute('/_public/handle')({
  validateSearch: (search: Record<string, unknown>) => ({
    url: (search.url as string | undefined) ?? undefined,
  }),
  component: HandleRedirect,
})

/** Matches the legacy work-request deep link, capturing the asset and (optional) company id. */
const WORK_REQUEST_RE = /^\/workRequests\?assetId=([^&]+)(?:&customerId=(.+))?$/

function HandleRedirect() {
  const { t } = useTranslation('assets')
  const { url } = Route.useSearch()
  const navigate = useNavigate()

  useEffect(() => {
    if (!url) {
      void navigate({ to: '/', replace: true })
      return
    }

    const decoded = decodeURIComponent(url)
    if (decoded.startsWith('web+ynteq://')) {
      const path = decoded.replace('web+ynteq://', '')
      const match = WORK_REQUEST_RE.exec(path)
      if (match) {
        const assetId = match[1]
        const companyId = match[2]
        if (companyId) {
          void navigate({
            to: '/work-request/$companyId',
            params: { companyId },
            search: { assetId },
            replace: true,
          })
          return
        }
      }
    }

    // Any other legacy pattern (assets/{id}, workOrders/{id}, …) carries no company
    // id and can't be mapped deterministically — fall back to the app root.
    void navigate({ to: '/', replace: true })
  }, [url, navigate])

  return (
    <div className="container py-5 text-center text-secondary">
      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
      {t('shared.fields.redirecting')}
    </div>
  )
}
